import Debug from '@/util/debug';
import AutocompleteExtend from '@/directives/autocomplete-extend';
import 'vue-awesome/icons/close';
import 'vue-awesome/icons/spinner';
import * as TargetLocation from '@/models/target-location';
import offlineCheck from '@/mixins/offline-check';

const debug = Debug('locations-input');

const EVENT_ADD_LOCATION = 'addLocation';

const ItemTemplate = {
  props: { item: { required: true } },
  computed: {
    name: function computedName() {
      const reg = new RegExp(this.$parent.searchText, 'gi');
      const types = ['name', 'primary_city', 'region', 'country_code'];

      let content = '';
      types.forEach(t => {
        if (Object.prototype.hasOwnProperty.call(this.item, t)) {
          content += (content !== '' ? ', ' : '') + this.item[t];
        }
      });

      return content.replace(reg, (name) => `<span class="highlight">${name}</span>`);
    },
  },
  template: '<div v-html="name"></div>',
};

const possibleTypes = [
  TargetLocation.LOCATION_TYPE_ADDRESS,
  TargetLocation.LOCATION_TYPE_CITY,
  TargetLocation.LOCATION_TYPE_ZIP,
  TargetLocation.LOCATION_TYPE_REGION,
  TargetLocation.LOCATION_TYPE_COUNTRY,
];

export default {
  dependencies: [
    'locationsService',
  ],
  directives: [
    AutocompleteExtend,
  ],
  props: {
    'min-length': {
      type: Number,
      default: 2,
    },
    debounce: {
      type: Number,
      default: 300,
    },
    availableCountries: Array,
    availableTypes: Array,
    selectedLocations: Array,
    disabledButtons: Boolean,
  },
  mixins: [offlineCheck],
  data() {
    return {
      type: null,
      items: [],
      selected: null,
      expand: false,
      itemTemplate: ItemTemplate,
      loading: false,
      disabled: false,
    };
  },
  computed: {
    locationTypes() {
      return this.availableTypes.filter(t => possibleTypes.indexOf(t) !== -1);
    },
  },
  watch: {
    disabledButtons: function watchDisabled(value) {
      this.disabled = value;
    },
    selected: function watchSelected(loc) {
      const fin = () => this.type = null;

      debug.info('selected item: %j', loc);
      if (loc) {
        if (loc.type === TargetLocation.LOCATION_TYPE_ADDRESS) {
          this.locationsService.metadataGoogle(loc)
            .then(newLoc => {
              this.$emit(EVENT_ADD_LOCATION, TargetLocation.fromGoogle(newLoc));
            })
            .then(fin)
            .catch(err => { debug.error(err); fin(); });
        } else {
          this.locationsService.coordinatesFacebook(loc)
            .then(coords => {
              const data = !this.isOnline
                ? TargetLocation.fromOfflineLocation(loc)
                : TargetLocation.fromFacebookLocation(loc, coords);

              this.$emit(EVENT_ADD_LOCATION, data);
            })
            .then(fin)
            .catch(err => { debug.error(err); fin(); });
        }
      }
    },
    type: function watchType(newType) {
      debug.info('target type: %s', newType);
      this.items = [];

      if (newType === TargetLocation.LOCATION_TYPE_COUNTRY
          && this.availableCountries.length === 1) {
        this.disabled = true;
        this.type = null;

        this.locationsService.getMetadata({ type: newType, key: this.availableCountries[0] })
          .then(meta => {
            const data = !this.isOnline
              ? TargetLocation.fromOfflineLocation(meta)
              : TargetLocation.fromFacebookMetadata(meta);
            this.$emit(EVENT_ADD_LOCATION, data);
          })
          .catch(err => debug.error(err));
      } else {
        this.selected = null;
        this.$nextTick(() => {
          if (this.$refs.autocomplete) {
            this.$refs.autocomplete.$el.querySelector('input').focus();
          }
        });
      }
    },
  },
  methods: {
    onUpdateItems(search) {
      this.loading = true;
      const fin = () => this.loading = false;

      debug.info('onUpdateItems: %s', search);
      if (this.type === TargetLocation.LOCATION_TYPE_ADDRESS) {
        const chunk = (arr, size) => arr.reduce((chunks, el, i) => (i % size
          ? chunks[chunks.length - 1].push(el)
          : chunks.push([el])) && chunks, []);

        const countryChunks = chunk(this.availableCountries, 5);

        Promise.all(countryChunks.map(c => this.locationsService.autocompleteGoogle(search, c)))
          .then(res => res.reduce((acc, val) => acc.concat(val), []))
          .then(res => {
            this.items = res;
            fin();
          })
          .catch(err => {
            fin();
            throw err;
          });
      } else {
        const promises = [];
        if (this.type === TargetLocation.LOCATION_TYPE_COUNTRY) {
          promises.push(this.locationsService.autocompleteLocations(search, this.type, null));
        } else {
          this.availableCountries.forEach(code => {
            promises.push(this.locationsService.autocompleteLocations(search, this.type, code));
          });
        }

        Promise.all(promises)
          .then(locations => {
            try {
              this.items = locations
                .reduce((acc, loc) => [...acc, ...loc])
                .filter(i => this.selectedLocations.findIndex(l => l.facebookId === i.key) === -1
                            && this.availableCountries.indexOf(i.country_code) >= 0);
            } catch (e) {
              this.items = [];
            }
            fin();
          })
          .catch(err => {
            fin();
            throw err;
          });
      }
    },
    onChange() {
      this.items = [];
    },
    getLocationLabel(item) {
      return TargetLocation.getFullName(item);
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/locations-input/LocationsInput.js